import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket debe ser usado dentro de un SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, forceLogout } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isForceLogout, setIsForceLogout] = useState(false);

  const socketRef = useRef(null);

  const handleForceLogout = useCallback(async (data) => {
    console.log('[SOCKET] Ejecutando force-logout para usuario:', user._id);
    setIsForceLogout(true);

    if (socketRef.current) {
      console.log('[SOCKET] Desconectando socket');
      socketRef.current.disconnect();
      socketRef.current.close();
    }

    console.log('[SOCKET] Limpiando almacenamiento');
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('[SOCKET] Error al limpiar almacenamiento:', error);
    }

    if (typeof forceLogout === 'function') {
      console.log('[SOCKET] Ejecutando forceLogout');
      try {
        await forceLogout(data.reason || 'server_request');
      } catch (error) {
        console.error('[SOCKET] Error al ejecutar forceLogout:', error);
      }
    }

    console.log('[SOCKET] Redirigiendo a login');
    window.location.href = '/login?reason=forced&timestamp=' + Date.now();
  }, [user?._id, forceLogout]);

  useEffect(() => {
    if (!user?._id || isForceLogout) return;

    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
    const newSocket = io(socketUrl, {
      path: '/ws/',
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true,
      query: {
        userId: user._id,
        token: localStorage.getItem('token')
      }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('[SOCKET] Conectado');
      setIsConnected(true);
      newSocket.emit('identify', {
        userId: user._id,
        token: localStorage.getItem('token')
      });
    });

    newSocket.on('connect_error', (error) => {
      console.error('[SOCKET] Error de conexión:', error);
      setIsConnected(false);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[SOCKET] Desconectado. Razón:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect' || reason === 'transport close') {
        newSocket.disconnect();
        newSocket.close();
      }
    });

    newSocket.on('identified', (data) => {
      console.log('[SOCKET] Identificación exitosa:', data);
    });

    const heartbeatInterval = setInterval(() => {
      if (newSocket.connected) {
        newSocket.emit('heartbeat', { userId: user._id });
      }
    }, 30000);

    newSocket.on('force-logout', async (data) => {
      console.log('[SOCKET] Recibido force-logout:', data);
      if (data.targetId !== user._id) {
        console.log('[SOCKET] Ignorando force-logout: no es para este usuario');
        return;
      }
      await handleForceLogout(data);
    });

    return () => {
      clearInterval(heartbeatInterval);
      socketRef.current = null;
      if (newSocket) {
        newSocket.disconnect();
        newSocket.close();
      }
    };
  }, [user?._id, forceLogout, isForceLogout, handleForceLogout]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};